/**
 * @fileoverview `<Spinner>` — indicateur de chargement indéterminé.
 *
 * À utiliser pour les actions courtes ou les états où aucune progression
 * chiffrée n'est disponible. Pour un état long et borné, préférer
 * `<Progress>`.
 */

import { Loader2 } from 'lucide-react';
import { cn } from '../lib/utils.ts';

type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps {
  /** Libellé visible et accessible. Si absent, le spinner est décoratif. */
  label?: string;
  size?: SpinnerSize;
  className?: string;
}

const SIZES: Record<SpinnerSize, string> = {
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-7',
};

/**
 * Spinner GNPC.
 *
 * @param props - Label visible/accessibile optionnel, taille et classe CSS.
 * @returns Indicateur de chargement indéterminé. Si `label` est fourni, le
 * wrapper porte `role="status"`.
 *
 * @example
 * ```tsx
 * <Spinner label="Vérification de votre paiement..." />
 * ```
 */
export function Spinner({ label, size = 'md', className }: SpinnerProps): React.JSX.Element {
  return (
    <span
      role={label ? 'status' : undefined}
      aria-live={label ? 'polite' : undefined}
      aria-hidden={label ? undefined : true}
      className={cn('text-muted-foreground inline-flex items-center gap-2 text-sm', className)}
    >
      <Loader2 className={cn('text-primary animate-spin', SIZES[size])} />
      {label && <span>{label}</span>}
    </span>
  );
}
